import React, { useState, useEffect } from 'react';
import { WEB_APP_URL, SECRET_TOKEN } from '../config';

// Cache helpers
const getCache = (key) => {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
};

const YearlyBookStats = ({ refreshToggle, selectedYear }) => {
  const [books, setBooks] = useState(() => getCache('books') || []);
  const [loading, setLoading] = useState(!getCache('books'));
  const [editingGoal, setEditingGoal] = useState(false);
  const [settings, setSettings] = useState(() => getCache('settings') || {});
  const targetYear = selectedYear || new Date().getFullYear();
  
  const getGoal = (year) => settings[`goal_${year}`] || 35;
  
  const [goal, setGoalState] = useState(35);
  
  // Fetch settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${WEB_APP_URL}?action=getSettings`);
        const data = await res.json();
        setSettings(data.settings || {});
        localStorage.setItem('settings', JSON.stringify(data.settings || {}));
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, []);
  
  // Update goal when year or settings change
  useEffect(() => {
    setGoalState(getGoal(targetYear));
  }, [targetYear, settings]);
  
  const saveGoal = async (year, value) => {
    const newSettings = { [`goal_${year}`]: value };
    setSettings(prev => ({ ...prev, ...newSettings }));
    try {
      await fetch(WEB_APP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          action: 'saveSettings',
          token: SECRET_TOKEN,
          settings: newSettings
        })
      });
    } catch (err) {
      console.error('Error saving settings:', err);
    }
  };

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await fetch(`${WEB_APP_URL}?action=getBooks`);
        const result = await response.json();
        setBooks(result.books || []);
      } catch (error) {
        console.error('Error fetching books for stats:', error);
      }
      setLoading(false);
      setLoading(false);
    };

    fetchBooks();
  }, [refreshToggle]); // Include refreshToggle so this effect re-runs when a book is added

  // Count books whose submission date is in targetYear
  const booksThisYear = books.filter(book => {
    if (book.date) {
      const year = new Date(book.date).getFullYear();
      return year === targetYear;
    }
    return false;
  }).length;

  const percentage = Math.min((booksThisYear / goal) * 100, 100);

  return (
    <div style={{ margin: '1em 0', textAlign: 'center' }}>
      {loading ? (
        <p>Loading stats...</p>
      ) : (
        <>
          <h3>Books Read in {targetYear}</h3>
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${percentage}%` }} key={targetYear}></div>
            </div>
            <p className="progress-text">
              {booksThisYear} of {editingGoal ? (
                <input
                  type="number"
                  value={goal}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setGoalState(val);
                  }}
                  onBlur={() => {
                    setEditingGoal(false);
                    saveGoal(targetYear, goal);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingGoal(false);
                      saveGoal(targetYear, goal);
                    }
                  }}
                  autoFocus
                  min="1"
                  style={{ width: '50px', textAlign: 'center' }}
                />
              ) : (
                <span 
                  onClick={() => setEditingGoal(true)} 
                  className="editable-goal"
                  title="Click to edit goal"
                >
                  {goal}
                </span>
              )} books {booksThisYear === 0 && '— time to start reading! 📚'}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default YearlyBookStats;
